import React, { useEffect, useState, useCallback, useRef } from "react";
import { useOnboarding as useOnboardingContext } from "../../hooks/useOnboarding";
import { OnboardingStep, OnboardingData } from "../../types/onboarding";
import { useOnboardingFlow } from "../../hooks/useOnboardingFlow";
import SetupAccount from "../../pages/onboarding/SetupAccount";
import TwoColumnLayout from "../layout/TwoColumnLayout";
import ProgressBar from "../ProgressBar";
import StepWrapper from "./StepWrapper";
import styles from "../../pages/onboarding/OnboardingFlow.module.css";
import containerStyles from "./OnboardingContainer.module.css";

import SelectIntegrations from "../../pages/onboarding/SelectIntegrations";
import PrepareUpdate from "../../pages/onboarding/PrepareUpdate";
import SendUpdate from "../../pages/onboarding/SendUpdate";
import PaymentPlans from "../../pages/onboarding/PaymentPlans";
import OnboardingMetrics from "../../pages/onboarding/OnboardingMetrics";
import SelectContacts from "../../pages/onboarding/SelectContacts";
import OnboardingErrorBoundary from "./OnboardingErrorBoundary";
import { useAuth } from "../../contexts/AuthContext";

/**
 * Props injected into each step component.
 */
interface StepActionProps {
  onNext: (data?: any) => void;
  onBack?: () => void;
}

/**
 * Helper function to determine current step from onboarding data.
 * Uses prerequisite validation to ensure user can only be on a step
 * if all previous steps are completed.
 */
const determineCurrentStepFromData = (data: OnboardingData): number => {
  if (!data) return 1;

  // If onboarding is complete, redirect will be handled elsewhere
  if (data.onboarding_complete) return 9;

  const completed = new Set(data.completed_steps || []);

  // Define steps with their own completion keys
  const stepCompletions: { step: number; completionKey: string }[] = [
    { step: 1, completionKey: "account_setup" },
    { step: 2, completionKey: "contacts_selection" },
    { step: 3, completionKey: "metrics_selection" },
    { step: 4, completionKey: "integrations_selection" },
    { step: 5, completionKey: "prepare_update" },
    { step: 6, completionKey: "send_update" },
    { step: 7, completionKey: "payment_complete" },
  ];

  // Find first step whose completion is missing
  for (const { step, completionKey } of stepCompletions) {
    if (!completed.has(completionKey)) {
      return step;
    }
  }

  // All steps complete but onboarding_complete not set → go to last step (payment)
  return 7;
};

/**
 * Helper function to check if onboarding is complete from data
 */
const isOnboardingCompleteFromData = (data: OnboardingData): boolean => {
  return data?.onboarding_complete === true;
};

const steps: OnboardingStep[] = [
  {
    id: 1,
    title: "Set Up Account",
    component: SetupAccount as React.ComponentType<StepActionProps>,
    imageSrc: "/assets/onboarding-img-1.png",
  },
  {
    id: 2,
    title: "Add Contacts",
    component: SelectContacts as React.ComponentType<StepActionProps>,
    imageSrc: "/assets/onboarding-img-3.png",
  },
  {
    id: 3,
    title: "Choose Metrics",
    component: OnboardingMetrics as React.ComponentType<StepActionProps>,
    imageSrc: "/assets/onboarding-img-5.png",
  },
  {
    id: 4,
    title: "Select Integrations",
    component: SelectIntegrations as React.ComponentType<StepActionProps>,
    imageSrc: "/assets/onboarding-img-6.png",
  },
  {
    id: 5,
    title: "Prepare Update",
    component: PrepareUpdate as React.ComponentType<StepActionProps>,
    imageSrc: "/assets/onboarding-img-6.png",
  },
  {
    id: 6,
    title: "Send Update",
    component: SendUpdate as React.ComponentType<StepActionProps>,
    imageSrc: "/assets/onboarding-img-6.png",
  },
  {
    id: 7,
    title: "Free Trial",
    component: PaymentPlans as React.ComponentType<StepActionProps>,
    imageSrc: "/assets/onboarding-img-6.png",
  },
];

interface OnboardingContainerState {
  currentStep: number;
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean;
  isTransitioning: boolean;
}

const OnboardingContainer: React.FC = () => {
  const totalSteps = steps.length;
  const {
    data,
    loading,
    error: loadError,
    save,
    complete,
    currentStep,
    isComplete,
  } = useOnboardingFlow();
  const { isOnboarded } = useAuth();
  const { isInitialized: contextInitialized } = useOnboardingContext();
  const [state, setState] = useState<OnboardingContainerState>({
    currentStep: 1,
    isLoading: true,
    error: null,
    isInitialized: false,
    isTransitioning: false,
  });

  // Debug mode toggle (persisted and URL-enabled)
  const [debugEnabled, setDebugEnabled] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem("onboarding_debug");
      if (stored != null) return stored === "true";
      const params = new URLSearchParams(window.location.search);
      return params.get("debug") === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("onboarding_debug", String(debugEnabled));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.debug("[Onboarding Debug] Failed to persist debug flag", e);
    }
  }, [debugEnabled]);

  // Console diff logs when data changes
  const prevDataRef = useRef<OnboardingData | null>(null);
  useEffect(() => {
    if (!debugEnabled) {
      prevDataRef.current = data;
      return;
    }
    const prev = prevDataRef.current;
    if (prev) {
      const keys: (keyof OnboardingData)[] = [
        "role",
        "metrics",
        "integrations",
        "step",
        "completed_steps",
      ];
      const diffs: Record<string, { before: any; after: any }> = {};
      for (const key of keys) {
        const before = (prev as any)[key];
        const after = (data as any)[key];
        const isEqual = JSON.stringify(before) === JSON.stringify(after);
        if (!isEqual) {
          diffs[key as string] = { before, after };
        }
      }
      if (Object.keys(diffs).length > 0) {
        // eslint-disable-next-line no-console
        console.log("[Onboarding Debug] Data changed:", diffs);
      }
    }
    prevDataRef.current = data;
  }, [data, debugEnabled]);

  // Initialize onboarding state using server data via useOnboardingFlow
  // Wait for both the context and the flow hook to be ready
  useEffect(() => {
    if (state.isInitialized) return;

    // Wait for both context initialization and server data loading
    if (!contextInitialized || loading) {
      setState((prev) => ({ ...prev, isLoading: true }));
      return;
    }

    const step = determineCurrentStepFromData(data);
    setState((prev) => ({
      ...prev,
      currentStep: step,
      isInitialized: true,
      isLoading: false,
      error: loadError,
    }));

    // Defensive persistence: if computed step differs from stored step, persist it
    // This ensures the correct step is saved after reload
    try {
      if (data.step !== step) {
        console.log(
          `[Onboarding] Correcting step mismatch: stored=${data.step}, computed=${step}`
        );
        void save({ step });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.debug("[Onboarding] Failed to persist step on init", e);
    }
  }, [contextInitialized, loading, data, loadError, state.isInitialized, save]);

  // Redirect to dashboard when onboarding is complete
  useEffect(() => {
    if (!state.isInitialized) return;
    if (isComplete || isOnboarded === true) {
      window.location.href = "/";
    }
  }, [isComplete, isOnboarded, state.isInitialized]);

  const handleNext = useCallback(
    async (stepData?: any) => {
      try {
        setState((prev) => ({ ...prev, error: null, isTransitioning: true }));

        const stepNames: { [key: number]: string } = {
          1: "account_setup",
          2: "contacts_selection",
          3: "metrics_selection",
          4: "integrations_selection",
          5: "prepare_update",
          6: "send_update",
          7: "payment_complete",
        };

        const name = stepNames[state.currentStep];
        const completed = new Set([...(data.completed_steps || [])]);
        if (name) completed.add(name);

        const nextStep = Math.min(state.currentStep + 1, totalSteps);

        // Merge step-specific data
        const partial: Partial<OnboardingData> = {
          ...stepData,
          step: nextStep,
          completed_steps: Array.from(completed),
        };

        await save(partial);

        if (state.currentStep >= totalSteps) {
          await complete();
          // redirect happens via effect
          return;
        }

        setState((prev) => ({
          ...prev,
          currentStep: nextStep,
          isTransitioning: false,
        }));
      } catch (e) {
        setState((prev) => ({
          ...prev,
          error: "Failed to save progress. Please try again.",
          isTransitioning: false,
        }));
      }
    },
    [state.currentStep, data.completed_steps, totalSteps, save, complete]
  );

  const handleBack = useCallback(() => {
    if (state.currentStep > 1) {
      const newStep = state.currentStep - 1;
      setState((prev) => ({
        ...prev,
        currentStep: newStep,
        isTransitioning: false,
      }));
      // Persist step when navigating back
      try {
        void save({ step: newStep });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.debug("[Onboarding] Failed to persist step on back", e);
      }
    }
  }, [state.currentStep]);

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  // Keep backend step in sync with local step changes (e.g., deep-links or programmatic changes)
  useEffect(() => {
    if (!state.isInitialized) return;
    if (data.step !== state.currentStep) {
      try {
        void save({ step: state.currentStep });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.debug("[Onboarding] Failed to sync step", e);
      }
    }
  }, [state.currentStep, state.isInitialized, data.step]);

  // Show loading state while initializing
  if (state.isLoading || !state.isInitialized) {
    return (
      <main className={styles.main}>
        <div className={`${styles.card} ${containerStyles.container}`}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "3rem",
              flexDirection: "column",
              gap: "1rem",
            }}
          >
            <div className={containerStyles.spinner} />
            <p>Setting up your onboarding experience...</p>
          </div>
        </div>
      </main>
    );
  }

  const currentStepData = steps.find((s) => s.id === state.currentStep);
  if (!currentStepData) {
    return (
      <main className={styles.main}>
        <div className={`${styles.card} ${containerStyles.container}`}>
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <p>Step not found. Please refresh the page.</p>
          </div>
        </div>
      </main>
    );
  }

  const StepComponent =
    currentStepData.component as React.ComponentType<StepActionProps>;
  const progressPercentage = (state.currentStep / totalSteps) * 100;

  return (
    <OnboardingErrorBoundary>
      <div className={containerStyles.onboardingWrapper}>
        <main className={styles.main}>
          <div className={`${styles.card} ${containerStyles.container}`}>
            {state.error && (
              <div className={containerStyles.errorBanner}>
                <div className={containerStyles.errorContent}>
                  <strong>Error:</strong> {state.error}
                  <button
                    onClick={clearError}
                    className={containerStyles.errorClose}
                    aria-label="Dismiss error"
                  >
                    ×
                  </button>
                </div>
              </div>
            )}

            <TwoColumnLayout
              left={
                <>
                  <div className={containerStyles.stepHeader}>
                    <h1 className={styles.title}>{currentStepData.title}</h1>
                    <div className={styles.step}>
                      Step {state.currentStep} of {totalSteps}
                    </div>
                    <ProgressBar progress={progressPercentage} />
                  </div>
                  <StepWrapper>
                    <div
                      className={`${containerStyles.stepContent} ${
                        state.isTransitioning
                          ? containerStyles.transitioning
                          : ""
                      }`}
                    >
                      <StepComponent
                        onNext={handleNext}
                        onBack={state.currentStep > 1 ? handleBack : undefined}
                      />
                    </div>
                  </StepWrapper>
                </>
              }
              right={
                <img
                  src={currentStepData.imageSrc}
                  alt={`${currentStepData.title} illustration`}
                  className={containerStyles.illustration}
                />
              }
            />
          </div>
        </main>
      </div>
    </OnboardingErrorBoundary>
  );
};

export default OnboardingContainer;
